import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';
import { Button, Card, Input } from '../components/common';
import { forgotPassword } from '../services/api';
import MouseBubble from '../components/common/MouseBubble';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-dark-dark dark:bg-white flex items-center justify-center transition-colors duration-300 py-12 px-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-500 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white dark:text-dark-dark mb-4 transition-colors duration-300">
            Check Your Email
          </h1>
          <p className="text-gray-400 dark:text-gray-500 mb-6 transition-colors duration-300">
            If an account exists for <strong>{email}</strong>, we've sent password reset instructions.
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-6 transition-colors duration-300">
            Didn't receive the email? Check your spam folder.
          </p>
          <Button
            variant="primary"
            size="lg"
            onClick={() => setSuccess(false)}
            className="w-full"
          >
            Send Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-dark dark:bg-white flex items-center justify-center transition-colors duration-300 py-12 px-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white dark:text-dark-dark mb-2 transition-colors duration-300">
            Forgot Password?
          </h1>
          <p className="text-gray-400 dark:text-gray-500 transition-colors duration-300">
            Enter your email and we'll send you reset instructions
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0" />
            <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            leftIcon={<Mail className="w-5 h-5" />}
            placeholder="you@example.com"
            required
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        <p className="mt-6 text-center text-gray-400 dark:text-gray-500 transition-colors duration-300">
          Remember your password?{' '}
          <Link to="/login" className="text-accent dark:text-primary hover:underline font-medium">
            Sign in
          </Link>
        </p>
      </Card>
      <MouseBubble />
    </div>
  );
};

export default ForgotPasswordPage;

